import { axiosClient } from '@/lib/api-client';
import { axiosErrorFrom } from '@/lib/error-handling';
import { DEFAULT_PAGE_SIZE, PaginationRequest } from '@/lib/pagination';
import { QueryClient, QueryKey, UseInfiniteQueryOptions } from '@tanstack/react-query';

import { CSVUploadResponse, isNotNullish, PutSchedulesStatusBody } from '@airreading/common';
import {
  AttendancePolicyV2History,
  AdminSession,
  AdminSessionsResults,
  Announcement,
  ArchiveOrSoftDeleteScheduleBody,
  AssessmentContentSelectionOverride,
  AssessmentResult,
  BulkCreateManualSessionForStudentGroupsOptions,
  BulkMarkTeacherPaymentAsSent,
  BulkRefreshZoomLinksOptions,
  BulkSendTeacherPayment,
  BulkUpdateSessionFeedbackResults,
  BulkUpdateSessionFeedbacksOptions,
  BulkUpdateStudentFeedbackOptions,
  BulkAssignReturningStudentsOptions,
  BulkAssignReturningStudentsResults,
  BulkUpdateStudentLearningPathCsvResult,
  BulkUpdateStudentsOptions,
  ClassTime,
  ClassTimeScheduleRow,
  Coach,
  CoachAttendanceSessionsRollup,
  CoachAvailability,
  CoachAvailabilityType,
  CoachTqReview,
  CoachNonSessionPaymentExtended,
  CoachPaymentCsvSummaryLike,
  CoachPaymentReportDTO,
  CoachPaymentReportForPayCycle,
  CoachPortalSessionsResults,
  CoachViewableSessionFeedback,
  CoachViewableSessionFeedbacks,
  CoachViewableStudentGroupWithScheduleAndLevel,
  CoachWeeklyTeachingHours,
  CreateAssessmentContentOptions,
  CreateAssessmentOptions,
  CreateAssessmentResult,
  CreateClassesAndOnboardingSessionOptions,
  CreateClassesAndOnboardingSessionResults,
  CreateClassesAndRegularSessionsOptions,
  CreateClassesAndRegularSessionsResults,
  CreateClassTimeOptions,
  CoachCsvDryRunResult,
  UpsertCoachUsersCsvResult,
  CreateLearningPathOptions,
  CreateLessonOptions,
  CreateManualSessionForStudentGroupOptions,
  CreateNonSessionPayment,
  CreateOrgOptions,
  CreateScheduleOptions,
  CreateSchoolOptions,
  CreateSessionsForGroupsResults,
  CreateSessionsForStudentGroupsOptions,
  CreateStudentFeedbackOptions,
  CreateStudentGroupOptions,
  BulkSoftDeleteStudentGroupsOptions,
  BulkUpdateStudentGroupReassignmentRequests,
  BulkUpdateStudentGroupReassignmentRequestsResults,
  BulkUpdateStudentGroupReassignmentRequestStatusOptions,
  BulkUpdateStudentGroupReassignmentRequestStatusResults,
  CoachPauseReassignmentPreview,
  CreateCoachPauseReassignmentRequests,
  CreateCoachPauseReassignmentRequestsResults,
  CreateStudentGroupReassignmentRequest,
  CreateStudentUsersCsvResult,
  CreateSubstitutionByCoachRequest,
  CreateUserOptions,
  CreateVendor,
  EditBulkActionRecreateSessions,
  EditStudentGroupsBulkActionsOptions,
  FullCoach,
  FullStudent,
  GetAdminSessionsOptions,
  GetAnnouncementsModifier,
  GetAnnouncementsResults,
  GetAssessmentContentsResults,
  GetAssessmentsResults,
  GetAssignedSessionFeedbacksModifier,
  GetCoachAttendanceRollupResult,
  GetAvailabilityOverviewModifier,
  GetAvailabilityOverviewResults,
  GetCoachesAvailabilitiesModifier,
  GetCoachesAvailabilitiesResults,
  CoachPool,
  CreateCoachPool,
  GetCoachStatesResults,
  GetCoachesMissingGoogleMeetModifier,
  GetCoachesMissingGoogleMeetResult,
  GetCoachesGoogleMeetTqReviewModifier,
  GetCoachesGoogleMeetTqReviewResult,
  GetCoachesTqReviewModifier,
  GetCoachesTqReviewResult,
  CreateCoachTqReview,
  UpdateCoachGoogleMeetTqReview,
  UpdateCoachTqReviewStatus,
  UpdateStudentGroupReassignmentRequest,
  UpdateStudentGroupReassignmentRequestResponse,
  GetCoachesMissingZoomLicenseResult,
  GetCoachesModifier,
  GetCoachesResults,
  GetCoachPaymentAsCoachModifier,
  GetCoachPaymentModifier,
  GetCoachPaymentResult,
  GetCoachReassignedStudentGroupsResults,
  GetCoachTeachingHoursOptions,
  GetCoachSessionPaymentDetailsResult,
  GetCoachSessionPaymentFilterModifier,
  GetCoachSessionsOptions,
  GetCoachViewableSessionFeedbacksModifier,
  GetMyStudentHandoutFeedbacksResults,
  GetMyStudentHandoutFeedbacksModifier,
  GetLessonResults,
  GetLessonsPaginatedQuery,
  GetLessonsTypeaheadModifier,
  GetLessonsTypeaheadResults,
  GetMakeupDiagnosticStudentsModifier,
  CompleteCoachOnboardingStepBody,
  GetMeCoachResult,
  GetMeResult,
  GetMinimalOrgsResults,
  GetMinimalSchoolsResults,
  GetOrgsModifier,
  GetOrgsResults,
  GetPayCycleModifierSchema,
  GetPayCyclesResult,
  GetPendingStudentGroupReassignmentRequestsModifier,
  GetPendingStudentGroupReassignmentRequestsResults,
  GetStudentGroupReassignmentRequestsResults,
  RefreshSystemSuggestedCoachesResults,
  GetPaymentsCsvFilter,
  GetSchedulesOptions,
  GetSchedulesResult,
  GetSchoolModifier,
  GetSchoolReportsCsvModifier,
  GetSchoolReportsModifier,
  GetSchoolReportsResults,
  GetSchoolsModifier,
  GetSchoolsResults,
  GetSessionFeedbackOptions,
  GetSessionFeedbacksModifier,
  GetSessionsForStudentGroupOptions,
  GetStudentFeedbacksModifier,
  GetStudentFeedbacksResults,
  GetStudentGroupsAttendanceModifier,
  GetStudentGroupsAttendanceResults,
  GetStudentGroupsModifier,
  GetStudentReviewHistoryModifier,
  GetStudentReviewHistoryResults,
  GetStudentsCountModifier,
  GetStudentsCountResults,
  GetStudentSessionsOptions,
  GetStudentsModifier,
  GetStudentsNoFutureSessionsModifier,
  GetStudentsNoFutureSessionsResults,
  GetSchoolPortalStudentsModifier,
  GetSchoolPortalStudentsResults,
  SchoolPortalStudentStats,
  GetStudentsOfSchoolModifier,
  GetStudentsOfSchoolResults,
  GetStudentsResults,
  GetFullStudentsResults,
  GetStudentsWithGLARecordsModifier,
  GetStudentsWithGLARecordsResults,
  GetStudentsWithLearningIssuesModifier,
  GetStudentsWithLearningIssuesResults,
  GetStudentsWithMultipleSchedulesResults,
  GetSubSessionTeachersModifier,
  GetSubSessionTeachersResults,
  GetTotalSumModifier,
  GetUsersModifier,
  GetUsersResults,
  GetUsersTypeaheadModifier,
  GetUsersTypeaheadResults,
  InAppAssessmentResultSubmission,
  JobRefreshSchoolsDataResult,
  LearningPathWithLessons,
  LessonWithAssessments,
  MaskedBrexTransfer,
  Org,
  OrgDeleteEligibility,
  OrgWithSchoolsEdlinkIdentifiable,
  PlainSchedule,
  PurgeDataRequestResult,
  PurgeDistrictDataOptions,
  PurgeDryRunResult,
  PurgeDryRunTable,
  PurgeEnrolledStudents,
  PurgeSchoolDataRequestOptions,
  RecordSessionFeedbackTimeTrackingBody,
  RecreateSessionsSummary,
  Schedule,
  SchoolWithOrgAndScheduleEdlinkIdentifiable,
  SessionFeedbacksWithAssessment,
  SessionFeedbackWithAssessment,
  SessionGoogleMeet,
  SingleAdminSession,
  SingleUserFull,
  StudentFeedback,
  StudentGroupsWithScheduleAndLevel,
  StudentGroupWithScheduleAndLevel,
  StudentSessionsResults,
  SyncCoachVendor,
  toQueryStringValue,
  UpdateAnnouncementOptions,
  UpdateAssessmentContentOptions,
  UpdateAssessmentOptions,
  UpdateBulkSessionsOptions,
  UpdateBulkSessionsResults,
  UpdateClassTimeOptions,
  UpdateCoachAvailabilitiesOptions,
  UpdateCoachOptions,
  UpdateFutureSessionsOptions,
  UpdateLearningPathOptions,
  UpdateLessonOptions,
  UpdateMeCoachOptions,
  UpdateMeOptions,
  UpdateNonSessionPayment,
  UpdateOrgOptions,
  UpdatePaymentNotes,
  UpdateScheduleOptions,
  UpdateSchoolOptions,
  UpdateSessionFeedbackOptions,
  UpdateSessionOptions,
  UpdateSessionOriginalTeacherOptions,
  UpdateSessionStudentsOptions,
  UpdateSessionTeacherOptions,
  UpdateStudentGLAOptions,
  UpdateStudentGroupAuditOptions,
  UpdateStudentGroupBulkActionsResults,
  UpdateStudentGroupOptions,
  UpdateStudentOptions,
  UpdateUserOptions,
  UserRoleAssignmentOptions,
  UserWithRolesAndProfiles,
  UserDeleteEligibility,
  Vendor,
} from '@airreading/common/data';
import {
  type EdlinkDistrictEntry,
  type EdlinkOrgChartDryRunResult,
  EdlinkOrgChartImportSummary,
  type EdlinkOrgChartImportSummaryLike,
} from '@airreading/sync/edlink';
import {
  V1CoachImportSummary,
  type V1CoachImportSummaryLike,
  V1OrgChartImportSummary,
  type V1OrgChartImportSummaryLike,
  V1ScheduleImportSummary,
  type V1ScheduleImportSummaryLike,
  V1StudentGroupImportSummary,
  type V1StudentGroupImportSummaryLike,
} from '@airreading/sync/v1';
import { Temporal } from 'temporal-polyfill';

/**
 * This file is currently undergoing a migration. Each exported endpoint wrapper
 * function will gradually become a wrapped query, which is an endpoint wrapper
 * function paired with a query key computation function. Both of these
 * functions take the same arguments, and are often used in conjunction with
 * one another anyway, so we plan to eventually refactor all endpoint wrappers
 * into wrapped query form.
 *
 * In time, this message can be deleted. For complementary information, such as
 * what query key computation functions look like, consult `@/lib/queries`.
 */

/**
 * A lightweight set of query options that can be passed directly to the
 * `useQuery` hook and friends, or spread into a more complete `QueryOptions`
 * object for further use.
 */
export interface LightweightQueryOptions<Result> {
  queryKey: QueryKey;
  queryFn: () => Result;
}

export const emptyQueryOptions: LightweightQueryOptions<undefined> = {
  queryFn: () => {},
  queryKey: [],
};

/**
 * A query wrapper function around an endpoint function that returns a
 * `LightweightQueryOption` instead of immediately making the request. It takes
 * the same arguments as the endpoint function it wraps.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface Query<Arguments extends any[], Result> {
  /**
   * Produce a set of query options bound to the given arugments.
   */
  (...args: Arguments): LightweightQueryOptions<Result>;

  /**
   * The original query key computing function.
   */
  queryKeyFn: (...args: Arguments) => QueryKey;

  /**
   * The original endpoint function.
   */
  queryFn: (...args: Arguments) => Result;
}

/**
 * Create a query wrapper around an endpoint function.
 * @param f an endpoint function to wrap
 * @param keyFn a function that computes the `QueryKey`, given the same
 * arguments as the wrapped endpoint function. Consult [the TanStack Query guide
 * on Query Keys](https://tanstack.com/query/latest/docs/framework/react/guides/query-keys)
 * for more information.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function query<F extends (...args: any) => any>(
  f: F,
  keyFn: (...args: Parameters<F>) => QueryKey,
): Query<Parameters<F>, ReturnType<F>> {
  return Object.assign(
    (...args: Parameters<F>): LightweightQueryOptions<ReturnType<F>> => ({
      queryKey: keyFn(...args),
      queryFn: Object.defineProperty(() => f(...args), 'name', { value: f.name, writable: false }),
    }),
    {
      queryKeyFn: keyFn,
      queryFn: f,
    },
  );
}

/**
 * A lightweight set of infinite query options that can be passed directly to the
 * `useInfiniteQuery` hook, or spread into a more complete `InfiniteQueryOptions`
 * object for further use.
 */
export type LightweightInfiniteQueryOptions<Result> = Pick<
  UseInfiniteQueryOptions<Result, Error, Result, Result, QueryKey, number>,
  'queryKey' | 'queryFn' | 'initialPageParam' | 'getNextPageParam'
>;

/**
 * Helper function to create infinite query options from a regular query function.
 * This adapts a paginated query function for use with useInfiniteQuery.
 * @param queryFn a function that returns LightweightQueryOptions for a paginated endpoint
 * @param getDataArray a function that extracts the data array from the paginated response
 * @param pageSize the page size to use for infinite queries
 */
export function createInfiniteQueryOptions<Result extends { total: number; pageIndex: number; pageSize: number }>(
  queryFn: (pageIndex: number, pageSize: number) => LightweightQueryOptions<Promise<Result>>,
  getDataArray: (result: Result) => unknown[],
  pageSize: number = DEFAULT_PAGE_SIZE,
): LightweightInfiniteQueryOptions<Result> {
  // Get base options for the query key (use page 0, but the key should be the same for all pages)
  const baseOptions = queryFn(0, pageSize);
  // Extract the base query key (remove any page-specific parts if needed)
  const baseQueryKey = baseOptions.queryKey;

  return {
    queryKey: baseQueryKey,
    queryFn: async ({ pageParam }: { pageParam: number }) => {
      const options = queryFn(pageParam, pageSize);
      return options.queryFn();
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage: Result) => {
      const dataArray = getDataArray(lastPage);
      const hasMore = lastPage.pageIndex * lastPage.pageSize + dataArray.length < lastPage.total;
      return hasMore ? lastPage.pageIndex + 1 : undefined;
    },
  };
}

/**
 * Creates a version of an object that is prepared for query string flattening.
 */
function queryStringRecordFrom(o?: object): Record<string, string> {
  const result: Record<string, string> = {};
  if (!o) {
    return result;
  }
  for (const [key, value] of Object.entries(o)) {
    if (value) {
      result[key] = toQueryStringValue(value);
    }
  }
  return result;
}

/**
 * An empty array used by as fallback value for API calls that return an array.
 * This avoids each component having to create its own empty array which will be different reference each time it is rendered.
 */
export const EmptyArray: never[] = [];

export const getLesson = query(
  async (lessonId: string) => {
    return (await axiosClient.get<LessonWithAssessments>(`/lessons/${lessonId}`)).data;
  },
  (lessonId) => ['LESSON', lessonId],
);

export const getLessonAssessmentContentOverrides = query(
  async (lessonId: string) => {
    return (
      await axiosClient.get<AssessmentContentSelectionOverride[]>(`/lessons/${lessonId}/assessment-contents-override`)
    ).data;
  },
  (lessonId) => ['LESSON_ASSESSMENT_CONTENT_OVERRIDES', lessonId],
);

export const getLessons = query(
  async (req?: GetLessonsPaginatedQuery) => {
    return (await axiosClient.get<GetLessonResults>('/lessons', { params: { ...req } })).data;
  },
  (req) => ['LESSONS', req].filter(isNotNullish),
);

export const getLessonsForCoach = query(
  async (req?: GetLessonsPaginatedQuery) => {
    return (await axiosClient.get<GetLessonResults>('/lessons/coach', { params: { ...req } })).data;
  },
  (req) => ['LESSONS', req].filter(isNotNullish),
);

export const getLessonsTypeahead = query(
  async (req: GetLessonsTypeaheadModifier) => {
    return (
      await axiosClient.get<GetLessonsTypeaheadResults>('/lessons/typeahead', {
        params: queryStringRecordFrom(req),
      })
    ).data;
  },
  (req) => ['LESSONS_TYPEAHEAD', req],
);

export const createLesson = async (req: CreateLessonOptions) => {
  return (await axiosClient.post('/lessons', req)).data;
};

export const updateLesson = async ({ id, data }: { id: string; data: UpdateLessonOptions }) => {
  return (await axiosClient.put(`/lessons/${id}`, data)).data;
};

export const createLessonsByCsv = async (formData: FormData) => {
  return (
    await axiosClient.post<CSVUploadResponse>('/lessons/csv', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  ).data;
};

export const getLearningPath = query(
  async (learningPathId: string) => {
    return (await axiosClient.get<LearningPathWithLessons>(`/learning-paths/${learningPathId}`)).data;
  },
  (learningPathId) => ['LEARNING_PATH', learningPathId],
);

export const getLearningPaths = query(
  async () => {
    return (await axiosClient.get<LearningPathWithLessons[]>('/learning-paths')).data;
  },
  () => ['LEARNING_PATHS'],
);

export const createLearningPath = async (req: CreateLearningPathOptions) => {
  return (await axiosClient.post('/learning-paths', req)).data;
};

export const updateLearningPath = async ({ id, data }: { id: string; data: UpdateLearningPathOptions }) => {
  return (await axiosClient.put(`/learning-paths/${id}`, data)).data;
};

export const createLearningPathsByCsv = async (formData: FormData) => {
  return (
    await axiosClient.post<CSVUploadResponse>('/learning-paths/csv', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  ).data;
};

export const getUsers = query(
  async (req?: GetUsersModifier) => {
    return (
      await axiosClient.get<GetUsersResults>('/users', {
        params: queryStringRecordFrom(req),
      })
    ).data;
  },
  (req) => ['USERS', req].filter(isNotNullish),
);

export const getUsersTypeahead = query(
  async (req: GetUsersTypeaheadModifier) => {
    return (
      await axiosClient.get<GetUsersTypeaheadResults>('/users/typeahead', {
        params: queryStringRecordFrom(req),
      })
    ).data;
  },
  (req) => ['USERS_TYPEAHEAD', req],
);

export const getUser = async (userId: string) => {
  return (await axiosClient.get<SingleUserFull>(`/users/${userId}`)).data;
};

export const createUser = async (data: CreateUserOptions) => {
  return (await axiosClient.post<UserWithRolesAndProfiles>('/users', data)).data;
};

export const createStudentUsersByCsv = async (formData: FormData) => {
  return (
    await axiosClient.post<CreateStudentUsersCsvResult>('/users/students/csv', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  ).data;
};

export const updateEdlinkStudentUsersByCsv = async (formData: FormData) => {
  return (
    await axiosClient.post<CreateStudentUsersCsvResult>('/users/students/csv/edlink', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  ).data;
};

export const dryRunCoachUsersByCsv = async (formData: FormData) => {
  return (
    await axiosClient.post<CoachCsvDryRunResult>('/users/coaches/csv/dry-run', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  ).data;
};

export const upsertCoachUsersByCsv = async (formData: FormData) => {
  return (
    await axiosClient.post<UpsertCoachUsersCsvResult>('/users/coaches/csv', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  ).data;
};

export const createCoachPaymentsByCsv = async (formData: FormData) => {
  return (
    await axiosClient.post<CoachPaymentCsvSummaryLike>('/payments/csv', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  ).data;
};

function isCoachImportSummaryLike(data: unknown): data is V1CoachImportSummaryLike {
  return (
    typeof data === 'object' &&
    data !== null &&
    'failures' in data &&
    Array.isArray((data as { failures: unknown }).failures)
  );
}

export const importCoachUsersFromV1 = async () => {
  try {
    const result = await axiosClient.post<V1CoachImportSummaryLike>('/users/coaches/v1');
    return V1CoachImportSummary.fromJSON<V1CoachImportSummary>(result.data);
  } catch (error) {
    const data = axiosErrorFrom(error)?.response?.data;
    if (isCoachImportSummaryLike(data)) {
      return V1CoachImportSummary.fromJSON<V1CoachImportSummary>(data);
    }
    throw error;
  }
};

export const imporSchedulesFromV1 = async () => {
  const result = await axiosClient.post<V1ScheduleImportSummaryLike>('/schedules/sync');
  return V1ScheduleImportSummary.fromJSON(result.data);
};

export const imporStudentGroupsFromV1 = async () => {
  const result = await axiosClient.post<V1StudentGroupImportSummaryLike>('/student-groups/sync');
  return V1StudentGroupImportSummary.fromJSON(result.data);
};

export const importOrgsFromV1 = async () => {
  const result = await axiosClient.post<V1OrgChartImportSummaryLike>('/orgs/sync-v1');
  return V1OrgChartImportSummary.fromJSON(result.data);
};

export const updateUser = async ({ userId, data }: { userId: string; data: UpdateUserOptions }) => {
  return (await axiosClient.put<SingleUserFull>(`/users/${userId}`, data)).data;
};

export const updateUserRoles = async ({ userId, roles }: { userId: string; roles: UserRoleAssignmentOptions }) => {
  return (await axiosClient.put(`/users/roles/${userId}`, roles)).data;
};

export const deleteUser = async (userId: string) => {
  return (await axiosClient.delete(`/users/${userId}`)).data;
};

export const getUserDeleteEligibility = query(
  async (userId: string) => {
    return (await axiosClient.get<UserDeleteEligibility>(`/users/${userId}/can-delete`)).data;
  },
  (userId) => ['USER_DELETE_ELIGIBILITY', userId],
);

export const getStudents = query(
  async (req: Partial<GetStudentsModifier> = {}) => {
    return (
      await axiosClient.get<GetStudentsResults>('/students', {
        params: queryStringRecordFrom(req),
      })
    ).data;
  },
  (req) => ['STUDENTS', req].filter(isNotNullish),
);

export const getStudentsNoFutureSessions = query(
  async (req: Partial<GetStudentsNoFutureSessionsModifier> = {}) => {
    return (
      await axiosClient.get<GetStudentsNoFutureSessionsResults>('/students/no-future-sessions', {
        params: queryStringRecordFrom(req),
      })
    ).data;
  },
  (req) => ['STUDENTS', 'NO_FUTURE_SESSIONS', req].filter(isNotNullish),
);

export const getStudentsWithGLARecords = query(
  async (req: Partial<GetStudentsWithGLARecordsModifier> = {}) => {
    return (
      await axiosClient.get<GetStudentsWithGLARecordsResults>('/students/gla-records', {
        params: queryStringRecordFrom(req),
      })
    ).data;
  },
  (req) => ['STUDENTS', 'GLA_RECORDS', req].filter(isNotNullish),
);

export const getStudentsWithLearningIssues = query(
  async (req: Partial<GetStudentsWithLearningIssuesModifier> = {}) => {
    return (
      await axiosClient.get<GetStudentsWithLearningIssuesResults>('/students/learning-issue', {
        params: queryStringRecordFrom(req),
      })
    ).data;
  },
  (req) => ['STUDENTS', 'LEARNING_ISSUE', req].filter(isNotNullish),
);

export const getStudentsWithMultipleSchedules = query(
  async (req: Partial<GetStudentsModifier> = {}) => {
    return (
      await axiosClient.get<GetStudentsWithMultipleSchedulesResults>('/students/multiple-schedule', {
        params: queryStringRecordFrom(req),
      })
    ).data;
  },
  (req) => ['STUDENTS', 'MULTIPLE_SCHEDULE', req].filter(isNotNullish),
);

export const getMakeupDiagnosticStudents = query(
  async (req: Partial<GetMakeupDiagnosticStudentsModifier> = {}) => {
    return (
      await axiosClient.get<GetFullStudentsResults>('/students/makeup-diagnostic', {
        params: queryStringRecordFrom(req),
      })
    ).data;
  },
  (req) => ['STUDENTS', 'MAKEUP_DIAGNOSTIC', req].filter(isNotNullish),
);

export const getDroppedStudents = query(
  async (req: Partial<GetStudentsModifier> = {}) => {
    return (
      await axiosClient.get<GetFullStudentsResults>('/students/dropped', {
        params: queryStringRecordFrom(req),
      })
    ).data;
  },
  (req) => ['STUDENTS', 'DROPPED', req].filter(isNotNullish),
);

export const getStudentsCount = query(
  async (req: Partial<GetStudentsCountModifier> = {}) => {
    return (
      await axiosClient.get<GetStudentsCountResults>('/students/count', {
        params: queryStringRecordFrom(req),
      })
    ).data;
  },
  (req) => ['STUDENTS', 'COUNT', req].filter(isNotNullish),
);

export const getStudent = query(
  async (studentId: string) => {
    return (await axiosClient.get<FullStudent>(`/students/${studentId}`)).data;
  },
  (studentId) => ['STUDENT', studentId],
);

export const createStudentProfile = async ({ userId, data }: { userId: string; data: UpdateStudentOptions }) => {
  return (await axiosClient.post(`/students/${userId}`, data)).data;
};

export const updateStudentProfile = async ({ userId, data }: { userId: string; data: UpdateStudentOptions }) => {
  return (await axiosClient.put(`/students/${userId}`, data)).data;
};

export const updateStudentGLA = async ({ studentId, data }: { studentId: string; data: UpdateStudentGLAOptions }) => {
  return (await axiosClient.put(`/students/${studentId}/gla`, data)).data;
};

export const assignManualOnDemandGLA = async (studentId: string) => {
  return (await axiosClient.post(`/students/${studentId}/assign-manual-gla`)).data;
};

export const regenerateShortLink = async (studentId: string): Promise<{ shortCode: string }> => {
  return (await axiosClient.post<{ shortCode: string }>('/join-now/regenerate-short-link', { studentId })).data;
};

export const bulkUpdateStudentProfiles = async (data: BulkUpdateStudentsOptions) => {
  return (await axiosClient.put(`/students/bulk`, data)).data;
};

export const bulkAssignReturningStudents = async (
  data: BulkAssignReturningStudentsOptions,
): Promise<BulkAssignReturningStudentsResults> => {
  return (await axiosClient.put<BulkAssignReturningStudentsResults>(`/students/bulk/returning`, data)).data;
};

export const bulkUpdateStudentLearningPathByCsv = async (formData: FormData) => {
  return (
    await axiosClient.post<BulkUpdateStudentLearningPathCsvResult>('/students/csv/learning-path', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  ).data;
};

export const createCoachProfile = async ({ userId, data }: { userId: string; data: UpdateCoachOptions }) => {
  return (await axiosClient.post(`/coaches/${userId}`, data)).data;
};

export const updateCoachProfile = async ({ userId, data }: { userId: string; data: UpdateCoachOptions }) => {
  return (await axiosClient.put(`/coaches/${userId}`, data)).data;
};

export const refreshCoachZoomInfo = async (userId: string) => {
  return (await axiosClient.post<Coach>(`/coaches/refresh-zoom/${userId}`)).data;
};

export const getOrgs = query(
  async (req?: GetOrgsModifier) => {
    return (await axiosClient.get<GetOrgsResults>('/orgs', { params: queryStringRecordFrom(req) })).data;
  },
  (req) => ['ORGS', req].filter(isNotNullish),
);

export const getMinimalOrgs = query(
  async (req?: GetOrgsModifier) => {
    return (await axiosClient.get<GetMinimalOrgsResults>('/orgs/minimal', { params: queryStringRecordFrom(req) })).data;
  },
  (req) => ['MINIMAL_ORGS', req].filter(isNotNullish),
);

export function invalidateOrgQueries(queryClient: QueryClient) {
  return Promise.all([queryClient.invalidateQueries(getOrgs()), queryClient.invalidateQueries(getMinimalOrgs())]);
}

export const createOrg = async (data: CreateOrgOptions) => {
  return (await axiosClient.post<Org>('/orgs', data)).data;
};

export const importOrgFromEdlinkDistrictEntry = async (districtEntry: EdlinkDistrictEntry) => {
  const result = await axiosClient.post<EdlinkOrgChartImportSummaryLike>(`/orgs/edlink-import`, districtEntry);
  return EdlinkOrgChartImportSummary.fromJSON<EdlinkOrgChartImportSummary>(result.data);
};

export const dryRunImportOrgFromEdlinkDistrictEntry = async (districtEntry: EdlinkDistrictEntry) => {
  return (await axiosClient.post<EdlinkOrgChartDryRunResult>(`/orgs/edlink-import-dry-run`, districtEntry)).data;
};

export const syncOrgFromEdlink = async (orgId: string) => {
  const result = await axiosClient.post<EdlinkOrgChartImportSummaryLike>(`/orgs/edlink-sync/${orgId}`);
  return EdlinkOrgChartImportSummary.fromJSON<EdlinkOrgChartImportSummary>(result.data);
};

export const dryRunSyncOrgFromEdlink = async (orgId: string) => {
  return (await axiosClient.post<EdlinkOrgChartDryRunResult>(`/orgs/edlink-sync-dry-run/${orgId}`)).data;
};

export const getOrg = query(
  async (orgId: string) => {
    return (await axiosClient.get<OrgWithSchoolsEdlinkIdentifiable>(`/orgs/${orgId}`)).data;
  },
  (orgId) => ['ORGS', orgId],
);

export const updateOrg = async ({ id, data }: { id: string; data: UpdateOrgOptions }) => {
  return (await axiosClient.put<Org>(`/orgs/${id}`, data)).data;
};

export const deactivateOrg = async (orgId: string) => {
  return (await axiosClient.post<Org>(`/orgs/${orgId}/deactivate`)).data;
};

export const deleteOrg = async (orgId: string) => {
  return (await axiosClient.delete(`/orgs/${orgId}`)).data;
};

export const getOrgDeleteEligibility = query(
  async (orgId: string) => {
    return (await axiosClient.get<OrgDeleteEligibility>(`/orgs/${orgId}/can-delete`)).data;
  },
  (orgId) => ['ORG_DELETE_ELIGIBILITY', orgId],
);

export const getUnimportedEdlinkOrgEntries = query(
  async () => {
    return (await axiosClient.get<EdlinkDistrictEntry[]>('/orgs/unimported-edlink-orgs')).data;
  },
  () => ['ORGS', 'UNIMPORTED_EDLINK'],
);

export const createSchool = async (data: CreateSchoolOptions) => {
  return (await axiosClient.post('/schools', data)).data;
};

export const getSchools = query(
  async (req?: GetSchoolsModifier) => {
    return (await axiosClient.get<GetSchoolsResults>('/schools', { params: queryStringRecordFrom(req) })).data;
  },
  (req) => ['SCHOOLS', req].filter(isNotNullish),
);

export const getMinimalSchools = query(
  async (req?: GetSchoolsModifier) => {
    return (await axiosClient.get<GetMinimalSchoolsResults>('/schools/minimal', { params: queryStringRecordFrom(req) }))
      .data;
  },
  (req) => ['MINIMAL_SCHOOLS', req].filter(isNotNullish),
);

export const syncStudentFromEdlink = query(
  async (studentId: string) => {
    const result = await axiosClient.post<EdlinkOrgChartImportSummaryLike>(`/students/edlink-sync/${studentId}`);
    return EdlinkOrgChartImportSummary.fromJSON<EdlinkOrgChartImportSummary>(result.data);
  },
  (studentId) => ['USER', studentId],
);

export const dryRunSyncStudentFromEdlink = async (studentId: string) => {
  return (await axiosClient.post<EdlinkOrgChartDryRunResult>(`/students/edlink-sync-dry-run/${studentId}`)).data;
};

export const syncSchoolFromEdlink = async (schoolId: string) => {
  const result = await axiosClient.post<EdlinkOrgChartImportSummaryLike>(`/schools/edlink-sync/${schoolId}`);
  return EdlinkOrgChartImportSummary.fromJSON<EdlinkOrgChartImportSummary>(result.data);
};

export const dryRunSyncSchoolFromEdlink = async (schoolId: string) => {
  return (await axiosClient.post<EdlinkOrgChartDryRunResult>(`/schools/edlink-sync-dry-run/${schoolId}`)).data;
};

export const getSchool = query(
  async (schoolId: string, req?: GetSchoolModifier) => {
    return (
      await axiosClient.get<SchoolWithOrgAndScheduleEdlinkIdentifiable>(`/schools/${schoolId}`, {
        params: queryStringRecordFrom(req),
      })
    ).data;
  },
  (schoolId, req) => ['SCHOOL', schoolId, req].filter(isNotNullish),
);

export const getStudentsOfSchool = query(
  async (schoolId: string, req: Partial<GetStudentsOfSchoolModifier> = {}) => {
    return (
      await axiosClient.get<GetStudentsOfSchoolResults>(`/students/of-school/${schoolId}`, {
        params: queryStringRecordFrom(req),
      })
    ).data;
  },
  (schoolId, req) => ['STUDENTS_OF_SCHOOL', schoolId, req].filter(isNotNullish),
);

export const getSchoolPortalStudents = query(
  async (req: Partial<GetSchoolPortalStudentsModifier> = {}) => {
    return (
      await axiosClient.get<GetSchoolPortalStudentsResults>('/students/school-portal', {
        params: queryStringRecordFrom(req),
      })
    ).data;
  },
  (req) => ['SCHOOL_PORTAL_STUDENTS', req].filter(isNotNullish),
);

export const getSchoolPortalStudentStats = query(
  async () => {
    return (await axiosClient.get<SchoolPortalStudentStats>('/students/school-portal/stats')).data;
  },
  () => ['SCHOOL_PORTAL_STUDENT_STATS'],
);

export const getReviewHistory = query(
  async (studentId: string, req: Partial<GetStudentReviewHistoryModifier> = {}) => {
    return (
      await axiosClient.get<GetStudentReviewHistoryResults>(`/students/${studentId}/review-history`, {
        params: queryStringRecordFrom(req),
      })
    ).data;
  },
  (studentId, req) => ['REVIEW_HISTORY', studentId, req].filter(isNotNullish),
);

export const updateSchool = async ({ schoolId, data }: { schoolId: string; data: UpdateSchoolOptions }) => {
  return (await axiosClient.put(`/schools/${schoolId}`, data)).data;
};

export const purgeSchoolDataDryRun = async ({
  schoolId,
  table,
  part,
  data,
}: {
  schoolId: string;
  table: PurgeDryRunTable;
  part: number;
  data: PurgeSchoolDataRequestOptions;
}) => {
  return (
    await axiosClient.post<PurgeDryRunResult>(`/schools/${schoolId}/purge-data-dry-run/${table}`, data, {
      params: { part },
    })
  ).data;
};

export const purgeDistrictDataDryRun = async ({
  districtId,
  table,
  part,
  data,
}: {
  districtId: string;
  table: PurgeDryRunTable;
  part: number;
  data: PurgeDistrictDataOptions;
}) => {
  return (
    await axiosClient.post<PurgeDryRunResult>(`/districts/${districtId}/purge-data-dry-run/${table}`, data, {
      params: { part },
    })
  ).data;
};

export const purgeSchoolDataEnrolledStudents = async ({
  schoolId,
  data,
}: {
  schoolId: string;
  data: PurgeSchoolDataRequestOptions;
}) => {
  return (await axiosClient.post<PurgeEnrolledStudents>(`/schools/${schoolId}/purge-data-enrolled-students`, data))
    .data;
};

export const purgeDistrictDataEnrolledStudents = async ({
  districtId,
  data,
}: {
  districtId: string;
  data: PurgeDistrictDataOptions;
}) => {
  return (await axiosClient.post<PurgeEnrolledStudents>(`/districts/${districtId}/purge-data-enrolled-students`, data))
    .data;
};

export const purgeSchoolDataRequest = async ({
  schoolId,
  data,
}: {
  schoolId: string;
  data: PurgeSchoolDataRequestOptions;
}) => {
  return (await axiosClient.post<PurgeDataRequestResult>(`/schools/${schoolId}/purge-data-request`, data)).data;
};

export const purgeDistrictDataRequest = async ({
  districtId,
  data,
}: {
  districtId: string;
  data: PurgeDistrictDataOptions;
}) => {
  return (await axiosClient.post<PurgeDataRequestResult>(`/districts/${districtId}/purge-data-request`, data)).data;
};

export const refreshSchoolsData = async () => {
  return (await axiosClient.get<JobRefreshSchoolsDataResult>(`/job-execution/refresh-schools-data`)).data;
};

export const getMe = query(
  async () => {
    return (await axiosClient.get<GetMeResult>('/me')).data;
  },
  () => ['ME'],
);

export const getMeCoach = query(
  async () => {
    return (await axiosClient.get<GetMeCoachResult>('/me/coach')).data;
  },
  () => ['ME_COACH'],
);

export const updateMe = async (options: UpdateMeOptions) => {
  return (await axiosClient.put<GetMeResult>('/me', options)).data;
};

export const updateMeCoach = async (options: UpdateMeCoachOptions) => {
  return (await axiosClient.put<GetMeCoachResult>('/me/coach', options)).data;
};

export const patchCompleteCoachOnboardingStep = async (body: CompleteCoachOnboardingStepBody) => {
  return (await axiosClient.patch<GetMeCoachResult>('/me/coach/onboarding-steps', body)).data;
};

export const getClassTimesOfSchedule = query(
  async (scheduleId: string) => {
    return (await axiosClient.get<ClassTimeScheduleRow[]>(`/class-times/schedule/${scheduleId}`)).data;
  },
  (scheduleId) => ['CLASS_TIMES_OF_SCHEDULE', scheduleId],
);

export const getSchedule = async (scheduleId: string) => {
  return (await axiosClient.get<Schedule>(`/schedules/${scheduleId}`)).data;
};

export const getSchedules = query(
  async (query?: GetSchedulesOptions) => {
    return (await axiosClient.get<GetSchedulesResult>('/schedules', { params: { ...query } })).data;
  },
  (req) => ['SCHEDULES', req].filter(isNotNullish),
);

export const createSchedule = async (data: CreateScheduleOptions) => {
  return (await axiosClient.post<PlainSchedule>('/schedules', data)).data;
};

export const updateSchedule = async ({ id, data }: { id: string; data: UpdateScheduleOptions }) => {
  return (await axiosClient.put<PlainSchedule>(`/schedules/${id}`, data)).data;
};

export const createClassTime = async (data: CreateClassTimeOptions): Promise<ClassTime> => {
  return (await axiosClient.post<ClassTime>('/class-times', data)).data;
};

export const updateClassTime = async ({
  id,
  data,
}: {
  id: string;
  data: UpdateClassTimeOptions;
}): Promise<ClassTime> => {
  return (await axiosClient.put<ClassTime>(`/class-times/${id}`, data)).data;
};

export const deleteClassTime = async (id: string): Promise<void> => {
  await axiosClient.delete(`/class-times/${id}`);
};

export const updateSchedulesStatus = async (data: PutSchedulesStatusBody) => {
  return (await axiosClient.put<void>(`/schedules/status`, data)).data;
};

export const archiveOrSoftDeleteSchedule = async ({
  id,
  data,
}: {
  id: string;
  data: ArchiveOrSoftDeleteScheduleBody;
}) => {
  return (await axiosClient.put<void>(`/schedules/${id}/status`, data)).data;
};

export const createStudentLevelsByCsv = async (formData: FormData) => {
  return (
    await axiosClient.post<CSVUploadResponse>('/student-levels/csv', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  ).data;
};

export const deleteStudentLevel = async (studentLevelId: string) => {
  return (await axiosClient.delete(`/student-levels/${studentLevelId}`)).data;
};

export const getStudentGroup = query(
  async (studentGroupId: string, options?: { includeLevelChangeWarning?: boolean }) => {
    return (
      await axiosClient.get<StudentGroupWithScheduleAndLevel>(`/student-groups/${studentGroupId}`, {
        params: options,
      })
    ).data;
  },
  (studentGroupId, options) => ['STUDENT_GROUP', studentGroupId, options].filter(isNotNullish),
);

export const getStudentGroups = query(
  async (query?: GetStudentGroupsModifier) => {
    return (
      await axiosClient.get<StudentGroupsWithScheduleAndLevel>('/student-groups', {
        params: queryStringRecordFrom(query),
      })
    ).data;
  },
  (req) => ['STUDENT_GROUPS', req].filter(isNotNullish),
);

export const createStudentGroup = async (data: CreateStudentGroupOptions) => {
  return (await axiosClient.post('/student-groups', data)).data;
};

export const createStudentGroupReassignmentRequest = async (data: CreateStudentGroupReassignmentRequest) => {
  return (await axiosClient.post(`/student-groups/${data.studentGroupId}/reassignment-requests`, data)).data;
};

export const getCoachPauseReassignmentPreview = query(
  async (coachId: string) => {
    return (
      await axiosClient.get<CoachPauseReassignmentPreview>(
        `/student-groups/coach/${coachId}/pause-reassignment-preview`,
      )
    ).data;
  },
  (coachId) => ['COACH_PAUSE_REASSIGNMENT_PREVIEW', coachId],
);

export const createCoachPauseReassignmentRequests = async (data: CreateCoachPauseReassignmentRequests) => {
  return (
    await axiosClient.post<CreateCoachPauseReassignmentRequestsResults>(
      `/student-groups/coach/${data.coachId}/pause-reassignment-requests`,
      data,
    )
  ).data;
};

export const bulkUpdateStudentGroupReassignmentRequests = async (data: BulkUpdateStudentGroupReassignmentRequests) => {
  return (
    await axiosClient.patch<BulkUpdateStudentGroupReassignmentRequestsResults>(
      '/student-groups/reassignment-requests/bulk',
      data,
    )
  ).data;
};

export const bulkUpdateStudentGroupReassignmentRequestStatus = async (
  data: BulkUpdateStudentGroupReassignmentRequestStatusOptions,
) => {
  return (
    await axiosClient.patch<BulkUpdateStudentGroupReassignmentRequestStatusResults>(
      '/student-groups/reassignment-requests/bulk/status',
      data,
    )
  ).data;
};

export const getPendingStudentGroupReassignmentRequests = query(
  async (req?: GetPendingStudentGroupReassignmentRequestsModifier) => {
    return (
      await axiosClient.get<GetPendingStudentGroupReassignmentRequestsResults>(
        '/student-groups/reassignment-requests',
        {
          params: queryStringRecordFrom(req),
        },
      )
    ).data;
  },
  (req) => ['PENDING_STUDENT_GROUP_REASSIGNMENT_REQUESTS', req].filter(isNotNullish),
);

export const getStudentGroupReassignmentRequests = query(
  async (studentGroupId: string) => {
    return (
      await axiosClient.get<GetStudentGroupReassignmentRequestsResults>(
        `/student-groups/${studentGroupId}/reassignment-requests`,
      )
    ).data;
  },
  (studentGroupId) => ['STUDENT_GROUP_REASSIGNMENT_REQUESTS', studentGroupId],
);

/**
 * Recalculates and persists the system-suggested teacher for the given requests. Stored
 * suggestions go stale once a suggested teacher is assigned elsewhere, so the reassign modal
 * refreshes the ones it is about to act on.
 */
export const refreshSystemSuggestedCoaches = query(
  async (reassignmentRequestIds: string[]) => {
    return (
      await axiosClient.post<RefreshSystemSuggestedCoachesResults>(
        '/student-groups/reassignment-requests/refresh-suggested',
        { reassignmentRequestIds },
      )
    ).data;
  },
  (reassignmentRequestIds) => ['REFRESH_SYSTEM_SUGGESTED_COACHES', [...reassignmentRequestIds].sort()],
);

export const updateStudentGroupReassignmentRequest = async ({
  reassignmentRequestId,
  data,
}: {
  reassignmentRequestId: string;
  data: UpdateStudentGroupReassignmentRequest;
}) => {
  return (
    await axiosClient.patch<UpdateStudentGroupReassignmentRequestResponse>(
      `/student-groups/reassignment-requests/${reassignmentRequestId}`,
      data,
    )
  ).data;
};

export const updateStudentGroup = async ({ id, data }: { id: string; data: UpdateStudentGroupOptions }) => {
  return (await axiosClient.put(`/student-groups/${id}`, data)).data;
};

export const softDeleteStudentGroup = async (id: string): Promise<void> => {
  await axiosClient.post(`/student-groups/${id}/soft-delete`);
};

export const bulkSoftDeleteStudentGroups = async (data: BulkSoftDeleteStudentGroupsOptions): Promise<void> => {
  await axiosClient.post('/student-groups/bulk/soft-delete', data);
};

export const createSessions = async (data: CreateSessionsForStudentGroupsOptions) => {
  return (await axiosClient.post<CreateSessionsForGroupsResults>('/sessions/student-groups', data)).data;
};

export const bulkUpdateStudentGroups = async (data: EditStudentGroupsBulkActionsOptions) => {
  return (await axiosClient.post<UpdateStudentGroupBulkActionsResults>(`/student-groups/bulk`, data)).data;
};

export const getRecreateSessionsSummary = async (
  data: EditBulkActionRecreateSessions,
): Promise<RecreateSessionsSummary> => {
  return (await axiosClient.post<RecreateSessionsSummary>(`/student-groups/bulk/recreate-summary`, data)).data;
};

export const createManualSession = async ({
  studentGroupId,
  data,
}: {
  studentGroupId: string;
  data: CreateManualSessionForStudentGroupOptions;
}) => {
  return (await axiosClient.post(`/sessions/student-groups/${studentGroupId}/manual`, data)).data;
};

export const groupStudentsAndCreateOnboardingSessions = async (data: CreateClassesAndOnboardingSessionOptions) => {
  return (await axiosClient.post<CreateClassesAndOnboardingSessionResults>('/sessions/onboarding-sessions', data)).data;
};

export const groupStudentsAndCreateRegularSessions = async (data: CreateClassesAndRegularSessionsOptions) => {
  return (await axiosClient.post<CreateClassesAndRegularSessionsResults>('/sessions/regular-sessions', data)).data;
};

export const bulkCreateManulSessionForStudentGroups = async (data: BulkCreateManualSessionForStudentGroupsOptions) => {
  return (await axiosClient.post<CreateSessionsForGroupsResults>('/sessions/student-groups/bulk', data)).data;
};

export const getSessionsForStudentGroup = query(
  async (studentGroupId: string, query?: GetSessionsForStudentGroupOptions) => {
    return (
      await axiosClient.get<AdminSession[]>(`/sessions/student-groups/${studentGroupId}`, {
        params: { ...query },
      })
    ).data;
  },
  (studentGroupId, query) => ['SESSIONS_FOR_STUDENT_GROUP', studentGroupId, query].filter(isNotNullish),
);

export const getAdminSessions = query(
  async (query?: GetAdminSessionsOptions) => {
    const result = await axiosClient.get<AdminSessionsResults>('/sessions', { params: queryStringRecordFrom(query) });
    return result.data;
  },
  (req) => ['ADMIN_SESSIONS', req].filter(isNotNullish),
);

export const getAdminSession = query(
  async (sessionId: string) => {
    return (await axiosClient.get<SingleAdminSession>(`/sessions/${sessionId}`)).data;
  },
  (sessionId: string) => ['ADMIN_SESSION', sessionId],
);

const coachPortalSessionsQueryKey = (view: 'upcoming' | 'past' | 'calendar', query?: GetCoachSessionsOptions) =>
  ['MY_COACH_PORTAL_SESSIONS', view, query].filter(isNotNullish);

export const getCoachPortalUpcomingSessions = query(
  async (query?: GetCoachSessionsOptions) => {
    return (
      await axiosClient.get<CoachPortalSessionsResults>('/sessions/my/coach/upcoming', {
        params: queryStringRecordFrom(query),
      })
    ).data;
  },
  (query) => coachPortalSessionsQueryKey('upcoming', query),
);

export const getCoachPortalPastSessions = query(
  async (query?: GetCoachSessionsOptions) => {
    return (
      await axiosClient.get<CoachPortalSessionsResults>('/sessions/my/coach/past', {
        params: queryStringRecordFrom(query),
      })
    ).data;
  },
  (query) => coachPortalSessionsQueryKey('past', query),
);

export const getCoachPortalCalendarSessions = query(
  async (query?: GetCoachSessionsOptions) => {
    return (
      await axiosClient.get<CoachPortalSessionsResults>('/sessions/my/coach/calendar', {
        params: queryStringRecordFrom(query),
      })
    ).data;
  },
  (query) => coachPortalSessionsQueryKey('calendar', query),
);

export const invalidateCoachPortalSessionsQueries = (queryClient: {
  invalidateQueries: (filters: { queryKey: string[] }) => unknown;
}) => queryClient.invalidateQueries({ queryKey: ['MY_COACH_PORTAL_SESSIONS'] });

export const getMyStudentSessions = query(
  async (query: GetStudentSessionsOptions) => {
    return (await axiosClient.get<StudentSessionsResults>('/sessions/my/student', { params: { ...query } })).data;
  },
  (query) => ['MY_STUDENT_SESSIONS', query].filter(isNotNullish),
);

export const updateFutureSessions = async (payload: UpdateFutureSessionsOptions) => {
  return (await axiosClient.put('/sessions/update-future', payload)).data;
};

export const updateSessionDetails = async ({ id, data }: { id: string; data: UpdateSessionOptions }) => {
  return (await axiosClient.put(`/sessions/${id}`, data)).data;
};

export const deleteSession = async ({ id }: { id: string }) => {
  return (await axiosClient.delete(`/sessions/${id}`)).data;
};

export const attendSession = async ({ id }: { id: string }) => {
  return (await axiosClient.put(`/sessions/${id}/attend`)).data;
};

export const recordSessionLessonLinkOpened = (sessionId: string) => {
  return axiosClient.post(`/sessions/${sessionId}/lesson-link-opened`);
};

export const updateSessionsDetails = async (data: UpdateBulkSessionsOptions) => {
  return (await axiosClient.put<UpdateBulkSessionsResults>('/sessions/status-by-ids', data)).data;
};

export const updateSessionStudents = async ({
  sessionId,
  payload,
}: {
  sessionId: string;
  payload: UpdateSessionStudentsOptions;
}) => {
  return (await axiosClient.put<SingleAdminSession>(`/sessions/${sessionId}/students`, payload)).data;
};

export const updateSessionTeacher = async ({
  sessionId,
  payload,
}: {
  sessionId: string;
  payload: UpdateSessionTeacherOptions;
}) => {
  return (await axiosClient.put<SingleAdminSession>(`/sessions/${sessionId}/session-teacher`, payload)).data;
};

export const updateSessionOriginalTeacher = async ({
  sessionId,
  payload,
}: {
  sessionId: string;
  payload: UpdateSessionOriginalTeacherOptions;
}) => {
  return (await axiosClient.put<SingleAdminSession>(`/sessions/${sessionId}/original-teacher`, payload)).data;
};

export const updateSessionSubNotes = async ({
  sessionId,
  subNotes,
}: {
  sessionId: string;
  subNotes: string | null | undefined;
}) => {
  return (await axiosClient.put(`/sessions/${sessionId}/sub-notes`, { subNotes })).data;
};

export const updatePinnedSubSession = async ({
  sessionId,
  pinnedSubSession,
}: {
  sessionId: string;
  pinnedSubSession: boolean | null;
}) => {
  return (await axiosClient.put(`/sessions/${sessionId}/pinned-sub-session`, { pinnedSubSession })).data;
};

export const updateSessionGoogleMeetChecked = async ({
  sessionId,
  checked,
}: {
  sessionId: string;
  checked: boolean;
}): Promise<SessionGoogleMeet> => {
  return (await axiosClient.put<SessionGoogleMeet>(`/sessions/${sessionId}/google-meet-checked`, { checked })).data;
};

export const refreshSessionZoomLink = async (sessionId: string) => {
  return (await axiosClient.post(`/sessions/${sessionId}/refresh-zoom-link`)).data;
};

export const bulkRefreshZoomLinks = async (data: BulkRefreshZoomLinksOptions) => {
  return (await axiosClient.post<UpdateBulkSessionsResults>('/sessions/refresh-zoom-links-bulk', data)).data;
};

export const createSessionFeedbacks = async (sessionId: string) => {
  return (await axiosClient.post(`/session-feedbacks/${sessionId}`)).data;
};

export const updateSessionFeedback = async (data: UpdateSessionFeedbackOptions, signal?: AbortSignal) => {
  return (await axiosClient.put('/session-feedbacks', data, { signal })).data;
};

export const bulkUpdateSessionFeedbacks = async (data: BulkUpdateSessionFeedbacksOptions) => {
  return (await axiosClient.put<BulkUpdateSessionFeedbackResults>('/session-feedbacks/bulk', data)).data;
};

export const getSessionFeedbacksAssignedToCoach = query(
  async (req?: GetAssignedSessionFeedbacksModifier) => {
    return (
      await axiosClient.get<SessionFeedbacksWithAssessment>('/session-feedbacks/assigned', {
        params: queryStringRecordFrom(req),
      })
    ).data;
  },
  (data) => ['ASSIGNED_SESSION_FEEDBACKS', data].filter(isNotNullish),
);

export const getSessionFeedbacks = query(
  async (req?: GetSessionFeedbacksModifier) => {
    return (
      await axiosClient.get<SessionFeedbacksWithAssessment>('/session-feedbacks', {
        params: queryStringRecordFrom(req),
      })
    ).data;
  },
  (data) => ['SESSION_FEEDBACKS', data].filter(isNotNullish),
);

export const getSessionFeedback = query(
  async (data: GetSessionFeedbackOptions) => {
    return (
      await axiosClient.get<SessionFeedbackWithAssessment>(
        `/session-feedbacks/${data.sessionId}/${data.studentId}/${data.coachId}`,
      )
    ).data;
  },
  (data) => ['SESSION_FEEDBACK', data],
);

export const getMyStudentHandoutFeedbacks = query(
  async (req?: GetMyStudentHandoutFeedbacksModifier) => {
    return (
      await axiosClient.get<GetMyStudentHandoutFeedbacksResults>('/session-feedbacks/my', {
        params: queryStringRecordFrom(req),
      })
    ).data;
  },
  (data) => ['MY_STUDENT_HANDOUT_FEEDBACKS', data].filter(isNotNullish),
);

export const getCoachViewableSessionFeedbacks = query(
  async (req?: GetCoachViewableSessionFeedbacksModifier) => {
    return (
      await axiosClient.get<CoachViewableSessionFeedbacks>('/session-feedbacks/coach-viewable', {
        params: queryStringRecordFrom(req),
      })
    ).data;
  },
  (data) => ['COACH_VIEWABLE_SESSION_FEEDBACKS', data].filter(isNotNullish),
);

export const getCoachViewableSessionFeedback = query(
  async (data: { sessionId: string; studentId: string; coachId: string }) => {
    return (
      await axiosClient.get<CoachViewableSessionFeedback>(
        `/session-feedbacks/coach-viewable/${data.sessionId}/${data.studentId}/${data.coachId}`,
      )
    ).data;
  },
  (data) => ['COACH_VIEWABLE_SESSION_FEEDBACK', data],
);

export const getSessionFeedbackTimeTracking = query(
  async (data: GetSessionFeedbackOptions) => {
    return (
      await axiosClient.get<boolean>(
        `/session-feedbacks/${data.sessionId}/${data.studentId}/${data.coachId}/time-tracking`,
      )
    ).data;
  },
  (data) => ['SESSION_FEEDBACK_TIME_TRACKING', data.sessionId, data.studentId, data.coachId],
);

export const recordSessionFeedbackTimeTracking = (body: RecordSessionFeedbackTimeTrackingBody) => {
  return axiosClient.post('/session-feedbacks/record-first-started', body);
};

export const getStudentFeedbacks = query(
  async (req?: Partial<GetStudentFeedbacksModifier>) => {
    return (
      await axiosClient.get<GetStudentFeedbacksResults>('/student-feedbacks', {
        params: queryStringRecordFrom(req ?? {}),
      })
    ).data;
  },
  (req?) => ['STUDENT_FEEDBACKS', req].filter(isNotNullish),
);

export const createStudentFeedback = async (data: CreateStudentFeedbackOptions) => {
  return (await axiosClient.post<StudentFeedback>('/student-feedbacks', data)).data;
};

export const bulkUpdateStudentFeedbacks = async (data: BulkUpdateStudentFeedbackOptions) => {
  return (await axiosClient.put(`/student-feedbacks/bulk`, data)).data;
};

export const getAssessments = query(
  async () => {
    return (await axiosClient.get<GetAssessmentsResults>('/assessments')).data;
  },
  () => ['ASSESSMENTS'],
);

export const getAssessmentContents = query(
  async () => {
    return (await axiosClient.get<GetAssessmentContentsResults>('/assessments/contents')).data;
  },
  () => ['ASSESSMENT_CONTENTS'],
);

export const createAssessment = async (data: CreateAssessmentOptions) => {
  return (await axiosClient.post('/assessments', data)).data;
};

export const createAssessmentResult = async (data: CreateAssessmentResult) => {
  return (await axiosClient.post('/assessments/create-assessment-result', data)).data;
};

export const updateAssessment = async ({ id, data }: { id: string; data: UpdateAssessmentOptions }) => {
  return (await axiosClient.put(`/assessments/${id}`, data)).data;
};

/**
 * Saves one in-app timed reading result on its own, without touching the session feedback's
 * submission state.
 */
export const updateInAppAssessmentResult = async ({
  id,
  data,
}: {
  id: string;
  data: InAppAssessmentResultSubmission;
}) => {
  return (await axiosClient.put<AssessmentResult>(`/assessments/results/${id}`, data)).data;
};

/** Clears one completed in-app result and stores the teacher's reason. */
export const redoInAppAssessmentResult = async ({ id, reason }: { id: string; reason: string }) => {
  return (await axiosClient.post<AssessmentResult>(`/assessments/results/${id}/redo`, { reason })).data;
};

export const createAssessmentContent = async (data: CreateAssessmentContentOptions) => {
  return (await axiosClient.post('/assessments/contents', data)).data;
};

export const createAssessmentContentsByCsv = async (formData: FormData) => {
  return (
    await axiosClient.post<CSVUploadResponse>('/assessments/contents/csv', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  ).data;
};

export const updateAssessmentContent = async ({ id, data }: { id: string; data: UpdateAssessmentContentOptions }) => {
  return (await axiosClient.put(`/assessments/contents/${id}`, data)).data;
};

export const deleteAssessmentContent = async (assessmentContentId: string): Promise<void> => {
  await axiosClient.delete(`/assessments/contents/${assessmentContentId}`);
};

export const requestSubstitutionAsCoach = async (data: CreateSubstitutionByCoachRequest) => {
  return (await axiosClient.post(`/substitution-requests/by-coach`, data)).data;
};

export const getCoaches = query(
  async (req?: GetCoachesModifier) => {
    return (
      await axiosClient.get<GetCoachesResults>('/coaches', {
        params: queryStringRecordFrom(req),
      })
    ).data;
  },
  (req) => ['COACHES', req].filter(isNotNullish),
);

export const getCoach = query(
  async (coachId: string) => {
    return (await axiosClient.get<FullCoach>(`/coaches/${coachId}`)).data;
  },
  (coachId) => ['COACH', coachId],
);

export const getCoachAvailabilitiesForCoach = query(
  async (coachId: string) => {
    return (await axiosClient.get<CoachAvailability[]>(`/coach-availabilities/coach/${coachId}`)).data;
  },
  (coachId) => ['COACH_AVAILABILITIES', coachId],
);

export const getSubSessionTeachers = query(
  async (req: GetSubSessionTeachersModifier) => {
    return (
      await axiosClient.get<GetSubSessionTeachersResults>('/coaches/sub-teachers-available', {
        params: queryStringRecordFrom(req),
      })
    ).data;
  },
  (req) => ['SUB_SESSION_TEACHERS', req].filter(isNotNullish),
);

export const getCoachClasses = query(
  async (coachId: string, includeRemoved = false) => {
    return (
      await axiosClient.get<CoachViewableStudentGroupWithScheduleAndLevel[]>(
        `/student-groups/coach/${coachId}/future-classes`,
        { params: includeRemoved ? { includeRemoved: true } : undefined },
      )
    ).data;
  },
  (coachId, includeRemoved = false) => ['COACH_CLASSES', coachId, includeRemoved],
);

export const getCoachTqReviews = query(
  async (coachId: string) => {
    return (await axiosClient.get<CoachTqReview[]>(`/coaches/${coachId}/tq-reviews`)).data;
  },
  (coachId) => ['COACHES', coachId, 'TQ_REVIEWS'],
);

export const requestCoachAssessmentReview = async (coachId: string) => {
  return (await axiosClient.post(`/coaches/${coachId}/assessment-review-request`)).data;
};

export const cancelCoachAssessmentReviewRequest = async (coachId: string) => {
  return (await axiosClient.delete(`/coaches/${coachId}/assessment-review-request`)).data;
};

export const getCoachReassignedStudentGroups = query(
  async (coachId: string, req?: PaginationRequest) => {
    return (
      await axiosClient.get<GetCoachReassignedStudentGroupsResults>(`/student-groups/coach/${coachId}/reassigned`, {
        params: queryStringRecordFrom(req),
      })
    ).data;
  },
  (coachId, req) => ['COACH_REASSIGNED_STUDENT_GROUPS', coachId, req].filter(isNotNullish),
);

export const getStudentGroupsAttendance = query(
  async (req?: GetStudentGroupsAttendanceModifier) => {
    return (
      await axiosClient.get<GetStudentGroupsAttendanceResults>('/student-groups/coach/attendance', {
        params: queryStringRecordFrom(req),
      })
    ).data;
  },
  (req) => ['STUDENT_GROUPS_ATTENDANCE', req].filter(isNotNullish),
);

export const getStudentGroupAttendancePolicyV2History = query(
  async (studentGroupId: string | undefined) => {
    return (
      await axiosClient.get<AttendancePolicyV2History>(`/student-groups/${studentGroupId}/attendance-policy-v2/history`)
    ).data;
  },
  (studentGroupId) => ['STUDENT_GROUP_ATTENDANCE_POLICY_V2_HISTORY', studentGroupId].filter(isNotNullish),
);

export const getCoachAttendanceRollupsForGroups = query(
  async (data: CoachAttendanceSessionsRollup) => {
    return (
      await axiosClient.post<Record<string, GetCoachAttendanceRollupResult>>(
        '/student-groups/coach-attendance/rollups',
        data,
      )
    ).data;
  },
  () => ['COACH_ATTENDANCE_ROLLUPS'],
);

export const getPreviousSessionTeachers = query(
  async (studentGroupId: string) => {
    return (
      await axiosClient.get<{ id: string; fullNameOrEmail: string }[]>(
        `/sessions/student-groups/${studentGroupId}/teachers`,
      )
    ).data;
  },
  (studentGroupId: string) => ['PREVIOUS_SESSION_TEACHERS', studentGroupId],
);

export const getCoachTeachingHours = query(
  async (req: GetCoachTeachingHoursOptions) => {
    return (
      await axiosClient.get<CoachWeeklyTeachingHours>(`/sessions/coach/teaching-hours`, {
        params: queryStringRecordFrom(req),
      })
    ).data;
  },
  (req) => ['COACH_TEACHING_HOURS', req.coachId],
);

export const getCoachAvailabilities = query(
  async (opts?: { type?: CoachAvailabilityType }) => {
    return (
      await axiosClient.get<CoachAvailability[]>('/coach-availabilities', {
        params: { type: opts?.type },
      })
    ).data;
  },
  (opts) => ['COACH_AVAILABILITIES', opts?.type ?? 'all'],
);

export const getCoachesAvailabilities = query(
  async (req?: GetCoachesAvailabilitiesModifier) => {
    return (
      await axiosClient.get<GetCoachesAvailabilitiesResults>(`/coach-availabilities/all`, {
        params: queryStringRecordFrom(req),
      })
    ).data;
  },
  (req) => ['COACHES_AVAILABILITIES', req].filter(isNotNullish),
);

export const getCoachStates = query(
  async () => {
    return (await axiosClient.get<GetCoachStatesResults>('/coaches/states')).data;
  },
  () => ['COACH_STATES'],
);

export const getAvailabilityOverview = query(
  async (req?: GetAvailabilityOverviewModifier) => {
    return (
      await axiosClient.get<GetAvailabilityOverviewResults>(`/coach-availabilities/availability-overview`, {
        params: queryStringRecordFrom(req),
      })
    ).data;
  },
  (req) => ['AVAILABILITY_OVERVIEW', req].filter(isNotNullish),
);

export const updateCoachAvailabilities = async (
  data: UpdateCoachAvailabilitiesOptions,
  availabilityType: CoachAvailabilityType,
) => {
  return (
    await axiosClient.put('/coach-availabilities', data, {
      params: { type: availabilityType },
    })
  ).data;
};

export const createCoachPool = async (data: CreateCoachPool) => {
  return (await axiosClient.post<CoachPool>('/coach-pools', data)).data;
};

export const getAnnouncements = query(
  async (req?: GetAnnouncementsModifier) => {
    return (
      await axiosClient.get<GetAnnouncementsResults>('/announcements', {
        params: queryStringRecordFrom(req),
      })
    ).data;
  },
  (req) => ['ANNOUNCEMENTS', req].filter(isNotNullish),
);

export const getCoachesMissingZoomLicense = query(
  async (req?: GetCoachesModifier) => {
    return (
      await axiosClient.get<GetCoachesMissingZoomLicenseResult>('/coaches/missing-zoom-license', {
        params: queryStringRecordFrom(req),
      })
    ).data;
  },
  (req) => ['COACHES', 'MISSING_ZOOM_LICENSE', req].filter(isNotNullish),
);

export const getCoachesMissingGoogleMeet = query(
  async (req?: GetCoachesMissingGoogleMeetModifier) => {
    return (
      await axiosClient.get<GetCoachesMissingGoogleMeetResult>('/coaches/missing-google-meet', {
        params: queryStringRecordFrom(req),
      })
    ).data;
  },
  (req) => ['COACHES', 'MISSING_GOOGLE_MEET', req].filter(isNotNullish),
);

export const getCoachesGoogleMeetTqReview = query(
  async (req?: GetCoachesGoogleMeetTqReviewModifier) => {
    return (
      await axiosClient.get<GetCoachesGoogleMeetTqReviewResult>('/coaches/google-meet-tq-review', {
        params: queryStringRecordFrom(req),
      })
    ).data;
  },
  (req) => ['COACHES', 'GOOGLE_MEET_TQ_REVIEW', req].filter(isNotNullish),
);

export const getCoachesTqReview = query(
  async (req?: GetCoachesTqReviewModifier) => {
    return (
      await axiosClient.get<GetCoachesTqReviewResult>('/coaches/tq-review', {
        params: queryStringRecordFrom(req),
      })
    ).data;
  },
  (req) => ['COACHES', 'TQ_REVIEW', req].filter(isNotNullish),
);

export const createCoachTqReview = async (data: CreateCoachTqReview) => {
  return (await axiosClient.post('/coaches/tq-review', data)).data;
};

export const updateCoachTqReviewStatus = async ({
  reviewId,
  data,
}: {
  reviewId: string;
  data: UpdateCoachTqReviewStatus;
}) => {
  return (await axiosClient.patch(`/coaches/tq-review/${reviewId}`, data)).data;
};

export const updateCoachGoogleMeetTqReview = async ({
  coachId,
  data,
}: {
  coachId: string;
  data: UpdateCoachGoogleMeetTqReview;
}) => {
  return (await axiosClient.patch(`/coaches/${coachId}/google-meet-tq-review`, data)).data;
};

export const createAnnouncement = async (data: UpdateAnnouncementOptions) => {
  return (await axiosClient.post<Announcement>('/announcements', data)).data;
};

export const updateAnnouncement = async ({
  announcementId,
  data,
}: {
  announcementId: string;
  data: UpdateAnnouncementOptions;
}) => {
  return (await axiosClient.put<Announcement>(`/announcements/${announcementId}`, data)).data;
};

export const deleteAnnouncement = async (announcementId: string): Promise<void> => {
  await axiosClient.delete(`/announcements/${announcementId}`);
};

export const getOngoingPayments = query(
  async (req?: GetPayCycleModifierSchema) => {
    return (
      await axiosClient.get<CoachPaymentReportForPayCycle>(`/payments/ongoing`, {
        params: queryStringRecordFrom(req),
      })
    ).data;
  },
  (req) => ['PAYMENTS', 'ongoing', req].filter(isNotNullish),
);

export async function getPaymentsCsv(req?: GetPaymentsCsvFilter): Promise<void> {
  const response = await axiosClient.get('/payments/csv', {
    params: queryStringRecordFrom(req),
    responseType: 'blob',
  });

  const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;

  const contentDisposition = response.headers['content-disposition'] as string | undefined;
  const filenameMatch = contentDisposition?.match(/filename="?([^";]+)"?/);
  const interval = req?.filters?.payCycleInterval;
  const [startDate, endDate] = interval ? interval.split('/') : [];
  const today = Temporal.Now.plainDateISO().toString();
  const fallbackName =
    startDate && endDate
      ? `${startDate}_${endDate}_transactions_${today}.csv`
      : `teacher_pay_transactions_${today}.csv`;
  const filename = filenameMatch?.[1] ?? fallbackName;

  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export const getTotalSumFromLifetime = query(
  async (req?: GetTotalSumModifier) => {
    return (await axiosClient.get<number>(`/payments/total-sum`, { params: queryStringRecordFrom(req) })).data;
  },
  (req) => ['PAYMENTS', 'TOTAL_SUM', req].filter(isNotNullish),
);

export const getTotalSumFromLifetimeAsCoach = query(
  async (req?: GetTotalSumModifier) => {
    return (await axiosClient.get<number>('/coaches/payment/total-sum', { params: queryStringRecordFrom(req) })).data;
  },
  (req) => ['PAYMENTS', 'TOTAL_SUM', req].filter(isNotNullish),
);

export const getCoachPaymentAsCoach = query(
  async (req?: GetCoachPaymentAsCoachModifier) => {
    return (
      await axiosClient.get<GetCoachPaymentResult>(`/coaches/payment`, {
        params: queryStringRecordFrom(req),
      })
    ).data;
  },
  (req) => ['PAYMENTS', 'COACH', req].filter(isNotNullish),
);

export const listPayCycles = query(
  async () => {
    return (await axiosClient.get<GetPayCyclesResult[]>(`/payments/pay-cycle`)).data;
  },
  () => ['PAYCYCLE'],
);

export const createNonSessionPayment = async (data: CreateNonSessionPayment) => {
  return (await axiosClient.post<CreateNonSessionPayment>(`/coaches/${data.coachId}/payment`, data)).data;
};

export const updateNonSessionPayment = async ({ id, data }: { id: string; data: UpdateNonSessionPayment }) => {
  return (await axiosClient.patch<UpdateNonSessionPayment>(`/payments/non-session-payment/${id}`, data)).data;
};

export const getCoachSessionPayment = query(
  async (req: { coachId: string; filters: GetCoachSessionPaymentFilterModifier }) => {
    return (
      await axiosClient.get<GetCoachSessionPaymentDetailsResult>(`/coaches/${req.coachId}/payment/session`, {
        params: queryStringRecordFrom(req.filters),
      })
    ).data;
  },
  (req) => ['PAYMENTS', 'COACH', 'SESSION_PAYMENT', req.coachId, req.filters].filter(isNotNullish),
);

export const getCoachSessionPaymentAsCoach = query(
  async (req: { filters: GetCoachSessionPaymentFilterModifier }) => {
    return (
      await axiosClient.get<GetCoachSessionPaymentDetailsResult>(`/coaches/payment/session`, {
        params: queryStringRecordFrom(req.filters),
      })
    ).data;
  },
  (req) => ['PAYMENTS', 'COACH', 'SESSION_PAYMENT', req.filters].filter(isNotNullish),
);

export const getCoachUpcomingPayments = query(
  async () => {
    return (await axiosClient.get<CoachPaymentReportDTO[]>(`/coaches/payment/upcoming`)).data;
  },
  () => ['PAYMENTS', 'COACH', 'UPCOMING'].filter(isNotNullish),
);

export const getCoachNonSessionPayment = query(
  async ({ nonSessionPaymentId }: { nonSessionPaymentId: string }) => {
    return (
      await axiosClient.get<CoachNonSessionPaymentExtended>(`/payments/non-session-payment/${nonSessionPaymentId}`)
    ).data;
  },
  (nonSessionPaymentId) => ['PAYMENTS', 'COACH', 'NON_SESSION_PAYMENT', nonSessionPaymentId].filter(isNotNullish),
);

export const deleteNonSessionPayment = async ({ id }: { id: string }) => {
  return (await axiosClient.delete(`/payments/non-session-payment/${id}`)).data;
};

export const updatePaymentNotes = async ({ id, data }: { id: string; data: UpdatePaymentNotes }) => {
  return (await axiosClient.put(`/payments/${id}/notes`, data)).data;
};

export const bulkSendTeacherPayment = async (body: BulkSendTeacherPayment) => {
  return (await axiosClient.post('/payments/send-teacher-payment/bulk', body)).data;
};

export const bulkMarkTeacherPaymentAsSent = async (body: BulkMarkTeacherPaymentAsSent) => {
  return (await axiosClient.post('/payments/mark-teacher-payment-as-sent/bulk', body)).data;
};

export const getCoachPayment = query(
  async (req?: GetCoachPaymentModifier) => {
    return (
      await axiosClient.get<GetCoachPaymentResult>(`/coaches/${req?.filters?.coachId}/payment`, {
        params: queryStringRecordFrom(req),
      })
    ).data;
  },
  (req) => ['PAYMENTS', 'COACH', req?.filters?.coachId, req].filter(isNotNullish),
);

export const getCoachVendor = query(
  async () => {
    return (await axiosClient.get<Vendor | undefined>('/coaches/vendor')).data;
  },
  () => ['PAYMENTS', 'COACH', 'VENDOR'],
);
export const getCoachVendorPossibleMatch = query(
  async () => {
    return (await axiosClient.get<Vendor | undefined>('/coaches/vendor/possible-match')).data;
  },
  () => ['PAYMENTS', 'COACH', 'VENDOR', 'POSSIBLE_MATCH'],
);

export const getCoachVendorForAdmin = query(
  async (coachId?: string) => {
    return (await axiosClient.get<Vendor | undefined>(`/payments/vendor/${coachId}`)).data;
  },
  (coachId) => ['PAYMENTS', 'COACH', 'VENDOR', coachId].filter(isNotNullish),
);

export const syncCoachVendor = async (body: SyncCoachVendor) => {
  return (await axiosClient.post('/coaches/vendor/sync', body)).data;
};

export const createCoachVendor = async (body: CreateVendor) => {
  return (await axiosClient.post('/coaches/vendor/create', body)).data;
};

export const updateCoachVendor = async (body: CreateVendor) => {
  return (await axiosClient.put('/coaches/vendor', body)).data;
};

export const getSchoolReports = query(
  async (req?: GetSchoolReportsModifier) => {
    return (await axiosClient.get<GetSchoolReportsResults>('/school-reports', { params: queryStringRecordFrom(req) }))
      .data;
  },
  (req) => ['SCHOOL_REPORTS', req].filter(isNotNullish),
);

export async function getSchoolReportsCsv(req?: GetSchoolReportsCsvModifier): Promise<void> {
  const response = await axiosClient.get('/school-reports/csv', {
    params: queryStringRecordFrom(req),
    responseType: 'blob',
  });

  // Create a download link for the CSV file
  const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;

  const date = Temporal.Now.plainDateISO().toString();
  const filename = `school_reports_export_${date}.csv`;

  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export const updateStudentGroupAudit = async ({ id, data }: { id: string; data: UpdateStudentGroupAuditOptions }) => {
  return (await axiosClient.put(`/student-group-audit/${id}`, data)).data;
};

export const getTransfer = query(
  async (paySendId?: string) => {
    return (await axiosClient.get<MaskedBrexTransfer | undefined>(`/payments/transfers/${paySendId}`)).data;
  },
  (paySendId) => ['PAYMENTS', 'TRANSFERS', paySendId].filter(isNotNullish),
);
